/**
 * Đọc chuỗi `error` của một clip Seedance để hiện lý do hỏng ngay trên card.
 * Tách riêng (KHÔNG import antd) để unit-test được dưới jest của react-scripts (xem CLAUDE.md).
 *
 * 🔴 Vì sao cần: đo trên prod 13/08/2026 có **144 clip FAILED** mà UI không hề nói lý do — khung 9:16 chỉ hiện
 * "not generated" y như chưa từng bấm gen. Người vận hành bấm lại, tốn credit, rồi lại tưởng nút hỏng.
 *
 * 🔴 Vì sao KHÔNG diễn giải từng mã: bản trước map mỗi mã Seedance sang một câu "nên sửa gì" (sửa prompt / sửa
 * khung hình / sửa lời thoại…). Nhưng Seedance đẻ mã mới liên tục và cùng một mã có nhiều nguyên nhân, nên
 * map kiểu đó **đoán sai** — ví dụ `InputImageSensitiveContentDetected.PrivacyInformation` (một ẢNH ref bị nghi
 * người thật) từng bị gộp vào nhánh "Prompt bị chặn → sửa câu chữ", đẩy người dùng đi sửa nhầm chỗ.
 * Giờ **chỉ phân loại thứ chắc chắn** (chặn lúc gửi hay sau khi render ⇒ có tốn credit không) và **hiện NGUYÊN
 * VĂN lỗi Seedance** để người vận hành tự đọc `code` + `message` (message thường chỉ đích danh `content[N]`,
 * lý do "may contain real person" / "sensitive information"…).
 *
 * Chuỗi `error` từ backend có hai dạng (`seedanceClient.ts`):
 *   - `Seedance CREATE fail: <code> <message>`               ← trượt NGAY lúc gửi ⇒ chưa render ⇒ chưa tốn credit
 *   - `Seedance task <id> FAILED: {"…","error":{"code":"…"}}`  ← render xong mới bị từ chối ⇒ đã tính tiền (JSON cắt ~300 ký tự)
 */

/** Khâu hỏng — chỉ dùng để suy ra có tốn credit hay không, KHÔNG dùng để khuyên cách sửa. */
export type ClipFailStage = 'INPUT' | 'OUTPUT' | 'OTHER';

export interface ClipFailInfo {
    /** Mã raw của Seedance (vd `OutputVideoSensitiveContentDetected`). Rỗng nếu không bóc được. */
    code: string;
    stage: ClipFailStage;
    /** Nhãn cực ngắn cho badge 9:16 — trung lập, không đoán nguyên nhân. */
    label: string;
    /** Một câu trung lập: hỏng ở khâu nào + có tốn credit không + "đọc nguyên văn bên dưới". */
    hint: string;
    /** Lượt gen này có bị tính tiền không (chặn lúc gửi thì chưa render ⇒ không mất). */
    billed: boolean;
}

/** Bóc mã lỗi Seedance khỏi chuỗi error của cả hai dạng. '' nếu không thấy. */
export function parseClipErrorCode(error?: string | null): string {
    const s = String(error || '');
    // dạng OUTPUT: …FAILED: {"…","error":{"code":"OutputVideoSensitiveContentDetected",…
    const inJson = s.match(/"code"\s*:\s*"([A-Za-z0-9_.]+)"/);
    if (inJson) return inJson[1];
    // dạng INPUT: Seedance CREATE fail: InputTextSensitiveContentDetected The request failed…
    const inCreate = s.match(/CREATE fail:\s*([A-Za-z0-9_.]+)/);
    if (inCreate) return inCreate[1];
    return '';
}

/**
 * Diễn giải một clip FAILED — chỉ phân loại khâu hỏng + tính tiền, phần lý do để `raw` tự nói.
 * Trả `null` nếu clip không phải FAILED hoặc không có `error` (gọi được vô tư trong JSX).
 */
export function clipFailInfo(clip?: { status?: string | null; error?: string | null } | null): ClipFailInfo | null {
    if (!clip || clip.status !== 'FAILED') return null;
    const raw = String(clip.error || '');
    if (!raw) return null;
    const code = parseClipErrorCode(raw);

    // Chặn NGAY lúc gửi: chưa render ⇒ chưa tốn credit. Bấm lại y nguyên sẽ hỏng y hệt.
    if (/CREATE fail/i.test(raw) || /^Input/.test(code)) {
        return {
            code: code || 'CREATE_FAIL',
            stage: 'INPUT',
            billed: false,
            label: 'Bị chặn lúc gửi',
            hint: 'Seedance từ chối NGAY lúc gửi — chưa render nên chưa tốn credit. Đọc nguyên văn lỗi bên dưới (mã + câu mô tả) để biết sửa chỗ nào; bấm lại y nguyên sẽ hỏng y hệt.',
        };
    }

    // Render xong mới bị từ chối: lượt này đã tính tiền.
    if (/\bFAILED:/i.test(raw) || /^Output/.test(code)) {
        return {
            code: code || '',
            stage: 'OUTPUT',
            billed: true,
            label: 'Lỗi sau khi render',
            hint: 'Seedance render xong rồi mới từ chối — lượt gen này đã tính tiền. Đọc nguyên văn lỗi bên dưới để biết cần đổi gì trước khi gen lại.',
        };
    }

    // Còn lại (thường là lỗi mạng lúc poll): nói thẳng, đừng đoán.
    return {
        code: code || '',
        stage: 'OTHER',
        billed: true,
        label: 'Gen lỗi',
        hint: 'Lượt gen này hỏng. Đọc nguyên văn lỗi bên dưới để biết lý do.',
    };
}

/**
 * Clip FAILED **mới nhất** trong danh sách (theo `createdAt`), để card biết nói lý do của lần bấm gần đây nhất.
 * Vẫn trả về cả khi event đã có bản DONE — người dùng cần biết variant vừa gen đã hỏng.
 */
export function latestFailedClip<T extends { status?: string | null; error?: string | null; createdAt?: string }>(
    clips?: T[] | null,
): T | null {
    const failed = (clips || []).filter((c) => c.status === 'FAILED' && c.error);
    if (!failed.length) return null;
    return failed.reduce((a, b) => (String(b.createdAt || '') > String(a.createdAt || '') ? b : a));
}
